import { useCallback } from "react";
import type { FC } from "react";
import { observer } from "mobx-react-lite";
import { FlowCanvas } from "../../components/FlowCanvas";
import type { FlowCanvasHandlers } from "../../components/FlowCanvas/nodes/context";
import type { ActionGroupType } from "../../types/candidate";
import type { CurrSection } from "./types";
import { useRootStore } from "../../stores/hooks";
import { canvasActiveActionHash } from "./active-section";

interface FlowCanvasPaneProps {
  activeItem: CurrSection;
  /** The selection Run History was opened from — while it is open `activeItem`
   * is the History section itself, so this is what stays highlighted. */
  prevActiveItem: CurrSection;
  /** The shared selection setter (AutomationForm's `setActiveItem`, i.e. the
   * same `onChange` the legacy `Sidebar` receives) — the single source of
   * truth for the active section in both canvases. */
  setActiveItem: (section: CurrSection) => void;
  /** AutomationForm's action-swap handler, the same one passed to `Sidebar`. */
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType: ActionGroupType,
  ) => void;
}

/**
 * Mount for the React Flow canvas (the automation canvas). Builds the
 * `FlowCanvasHandlers` from the same `setActiveItem`/`onChangeAction` the legacy
 * Sidebar/Branches are driven by, so selection stays funneled through the one
 * `activeItem` state rather than a parallel copy. `activeAction` is derived from
 * the active section (the canvas nodes only need the active action's hash for
 * highlight).
 */
export const FlowCanvasPane: FC<FlowCanvasPaneProps> = observer(
  ({ activeItem, prevActiveItem, setActiveItem, onChangeAction }) => {
    const store = useRootStore();
    const onClickTrigger = useCallback(
      () => setActiveItem({ type: "trigger", hash: "" }),
      [setActiveItem],
    );

    const onClickAction = useCallback(
      (hash?: string) => {
        if (hash !== undefined) setActiveItem({ type: "action", hash });
      },
      [setActiveItem],
    );

    // Selecting a block/branch/loop card also closes any open canvas menu
    // (action picker, kebab, action-change dropdown). onPaneClick only fires
    // on the empty canvas, so a click that lands on a block node would open
    // its settings without dismissing a menu opened elsewhere. These card
    // clicks are pure selection (their own kebab toggles stopPropagation), so
    // bumping the dismiss signal here is race-free.
    const onSelectBlock = useCallback(
      (instanceId: number) => {
        store.flowCanvas.dismissOpenMenus();
        setActiveItem({ type: "branches", instanceId });
      },
      [setActiveItem, store],
    );

    const onSelectBranch = useCallback(
      (instanceId: number, branchHash: string) => {
        store.flowCanvas.dismissOpenMenus();
        setActiveItem({ type: "branch", instanceId, branchHash });
      },
      [setActiveItem, store],
    );

    const onSelectNestedLoop = useCallback(
      (instanceId: number) => {
        store.flowCanvas.dismissOpenMenus();
        setActiveItem({ type: "nested-loop", instanceId });
      },
      [setActiveItem, store],
    );

    const onSelectLooping = useCallback(
      (hash: string) => {
        store.flowCanvas.dismissOpenMenus();
        setActiveItem({ type: "looping", hash });
      },
      [setActiveItem, store],
    );

    const handleChangeAction = useCallback<
      FlowCanvasHandlers["onChangeAction"]
    >(
      (integrationId, actionId, actionGroupType) =>
        onChangeAction(integrationId, actionId, actionGroupType ?? "actions"),
      [onChangeAction],
    );

    const activeAction = canvasActiveActionHash(activeItem, prevActiveItem);

    return (
      <FlowCanvas
        key={store.flowCanvas.viewportResetKey}
        activeItem={activeItem}
        activeAction={activeAction}
        onClickTrigger={onClickTrigger}
        onClickAction={onClickAction}
        onChangeAction={handleChangeAction}
        onSelectBlock={onSelectBlock}
        onSelectBranch={onSelectBranch}
        onSelectNestedLoop={onSelectNestedLoop}
        onSelectLooping={onSelectLooping}
      />
    );
  },
);
FlowCanvasPane.displayName = "FlowCanvasPane";
